import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Card, CardContent, CardHeader } from "@/components/_atoms/card";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { useTeamInviteAccept } from "@/domain/team/hooks/api/use-team-invite-accept";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { AvatarWorkspace } from "@/domain/workspace/components/avatar-workspace";
import { Workspace } from "@/domain/workspace/models/workspace-model";

import { TeamProp, UserProp } from "../routes/accept-invite-team.page";
import { signout } from "../services/auth.service";

export interface AcceptTeamInviteCardProps {
  token: string;
  workspace: Workspace;
  team: TeamProp;
  user: UserProp;
}

interface ContinueAction {
  label: string;
  execute: () => void;
}

export const AcceptTeamInviteCard: FC<AcceptTeamInviteCardProps> = ({ token, team, workspace, user }) => {
  const { acceptTeamInvitation, queryResult } = useTeamInviteAccept({
    team: { id: team.id },
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
  });

  const { currentUser } = useCurrentUserContext();
  const { t } = useTranslation("auth");
  const [continueAction, setContinueAction] = useState<ContinueAction | null>(null);

  function invitationIsForAuthenticatedAccount() {
    return currentUser && currentUser.email === user.email;
  }

  function handleAcceptInvitePressed() {
    acceptTeamInvitation({
      token: token,
    });
  }

  function onUpdateCompleted() {
    if (invitationIsForAuthenticatedAccount()) {
      setContinueAction({
        label: t("invite.continueToTeamPage"),
        execute: () => window.location.replace(new URL(`${window.location.origin}/${workspace.workspaceUrl}/teams/${team.id}`)),
      });
    } else if (currentUser && currentUser.email !== user.email) {
      setContinueAction({
        label: t("invite.continueToLogin"),
        execute: () => signout(),
      });
    } else {
      setContinueAction({
        label: t("invite.continueToLogin"),
        execute: () => window.location.replace(new URL(`${window.location.origin}`)),
      });
    }

    toast.success(t("invite.team.welcomeTitle", { name: team.name }), { description: t("invite.accepted") });
  }

  function onUpdateError() {
    toast.error(t("invite.inviteError"), { description: t("invite.inviteErrorDescription") });
  }

  return (
    <Card className="z-10 w-full max-w-xl overflow-hidden border p-10 shadow-xl">
      <CardHeader>
        <div className="flex flex-col gap-4">
          <AvatarWorkspace workspace={workspace} className="h-16 w-16 text-2xl" />
          <h2 className="text-xl">{t("invite.team.title", { team: team.name, workspace: workspace.name })}</h2>
          <p className="text-md font-light text-muted-foreground">
            {t("invite.arkDescription")}
          </p>
          <div className="w-full border-t border-solid" />
        </div>
      </CardHeader>
      <CardContent>
        {continueAction ? (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground" role="status">
              {t("invite.accepted")}
            </p>
            <Button
              autoFocus
              onClick={() => continueAction.execute()}
              variant="default"
              className="h-10 w-48 items-center justify-center text-sm"
            >
              {continueAction.label}
            </Button>
          </div>
        ) : (
          <div className="mb-10 mt-4 flex flex-col gap-4">
            <p className="mb-2 text-base font-light text-foreground">
              {t("invite.team.accept", { email: user.email })}
              {invitationIsForAuthenticatedAccount() ? "." : t("invite.team.acceptSuffix")}
            </p>
            <Button
              onClick={() => handleAcceptInvitePressed()}
              disabled={queryResult.isPending}
              variant="default"
              className={`${
                queryResult.isPending ? "cursor-not-allowed bg-muted" : ""
              } h-10 w-48 items-center justify-center text-sm  `}
            >
              {queryResult.isPending ? <LoadingDots color="#808080" /> : <p>{t("invite.acceptInvite")}</p>}
            </Button>
          </div>
        )}

        {currentUser && currentUser.email !== user.email && (
          <div className="mt-6 w-full border-t border-solid pt-2">
            <p className="text-sm font-light text-foreground">
              {t("invite.team.alreadyLoggedInAs", { email: currentUser.email, team: team.name })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
